"use client";
import React, { useEffect, useState } from "react";
import "./index.css";
import dynamic from "next/dynamic";

// Critical component - SSR enabled
import HeroSection from "./HeroSection";

// Below-the-fold components - SSR disabled for better initial load performance
const ServiceSection = dynamic(() => import("./ServiceSection"), { ssr: false });
const WhyChooseUs = dynamic(() => import("./WhyChoose"), { ssr: false });
const BlogsSection = dynamic(() => import("./BlogsSection"), { ssr: false });
const FollowOn = dynamic(() => import("./FollowOn"), { ssr: false });
const TestimonialSection = dynamic(() => import("@/components/common-components/testimonial/TestimonialSection"), { ssr: false });

import { useDispatch, useSelector } from "react-redux";
import { fetchSeoData } from "@/redux/seoSlice";
import DotLoaders from "@/components/common-components/DotLoaders";
import AboutUsSection from "@/components/common-components/about-us-section/AboutUsSection";
import BannerSection from "../../common-components/banner-section/BannerSection";

import { fetchPageData, fetchNavigationData } from "@/utlility/api-helpers";
import { allSubCategories } from "@/redux/treatmentSlice";
import FaqSection from "@/components/common-components/faqs/FaqSection";

const Homepage = ({ initialHomeData }) => {
  const dispatch = useDispatch();
  const { data: treatmentData } = useSelector((state) => state?.treatment?.treatmentList || {});
  const { featuresData: reduxFeaturesData, headerData } = useSelector((state) => state?.organization);
  const seoData = useSelector((state) => state?.seo?.seoData);

  // Helper to parse sections from data
  const parseHomeData = (homeResult) => {
    if (!homeResult) return {};
    const { section_content_url, section_content_item_url, data } = homeResult;
    const findSection = (slug) => data.find((section) => section.slug === slug);

    return {
      imageUrl: section_content_url || "",
      itemImageUrl: section_content_item_url || "",
      bannerContents: findSection("banner-section")?.sub_sections || [],
      whyUsContents: findSection("why-choose-us-section")?.section_contents || [],
      whyUsSubSections: findSection("why-choose-us-section")?.sub_sections || [],
      aboutContents: findSection("weaving-fashion-journey")?.section_contents || [],
      testimonialContents: findSection("testimonial")?.section_contents || [],
      latestBlogsContents: findSection("latest-blogs")?.section_contents || [],
      servicesContents: findSection("our-services")?.section_contents || [],
    };
  };

  // Helper to parse navigation data
  const parseNavData = (navResult) => {
    if (!navResult) return {};
    const defaultArea = navResult.market_area?.find((item) => item.is_default === true);
    const flattenedServices = (navResult.categories || []).flatMap(cat => 
      (cat.sub_categories || []).map(sub => ({
        ...sub,
        parent_category: {
          category_name: cat.category_name,
          category_slug: cat.category_slug
        }
      }))
    );
    return {
      serviceSection: flattenedServices,
      categoryImageUrl: navResult.category_image_url || "",
      defaultMarketArea: defaultArea || null,
    };
  };

  const initialParsedHome = parseHomeData(initialHomeData);
  const initialParsedNav = parseNavData(reduxFeaturesData);

  const [pageData, setPageData] = useState({
    bannerContents: initialParsedHome.bannerContents || [],
    whyUsContents: initialParsedHome.whyUsContents || [],
    whyUsSubSections: initialParsedHome.whyUsSubSections || [],
    aboutContents: initialParsedHome.aboutContents || [],
    testimonialContents: initialParsedHome.testimonialContents || [],
    latestBlogsContents: initialParsedHome.latestBlogsContents || [],
    servicesContents: initialParsedHome.servicesContents || [],
    imageUrl: initialParsedHome.imageUrl || "",
    itemImageUrl: initialParsedHome.itemImageUrl || "",
    defaultMarketArea: initialParsedNav.defaultMarketArea || null,
    serviceSection: initialParsedNav.serviceSection || [],
    categoryImageUrl: initialParsedNav.categoryImageUrl || "",
    homePageLoading: !initialHomeData,
    fetchDataLoading: !reduxFeaturesData,
  });

  useEffect(() => {
    const loadHomeData = async () => {
      try {
        const fetchHomePromise = initialHomeData ? Promise.resolve(initialHomeData) : fetchPageData("home-page");
        const fetchNavPromise = reduxFeaturesData ? Promise.resolve(reduxFeaturesData) : fetchNavigationData();

        const [homeResult, navResult] = await Promise.all([
          fetchHomePromise,
          fetchNavPromise
        ]);

        if (homeResult) {
          const parsed = parseHomeData(homeResult);
          setPageData(prev => ({
            ...prev,
            ...parsed,
            homePageLoading: false
          }));
        }

        if (navResult) {
          const parsed = parseNavData(navResult);
          setPageData(prev => ({
            ...prev,
            ...parsed,
            fetchDataLoading: false
          }));

          dispatch(allSubCategories({
            payload: {
              organization_key: process.env.NEXT_PUBLIC_ORGANIZATION_KEY,
              page: 1,
              limit: 6
            }
          }));
        }
      } catch (error) {
        setPageData(prev => ({ ...prev, homePageLoading: false, fetchDataLoading: false }));
      }
    };

    loadHomeData();

    if (!seoData) {
      dispatch(fetchSeoData({ organization_key: process.env.NEXT_PUBLIC_ORGANIZATION_KEY, slug: "home-page" }));
    }
  }, [dispatch, reduxFeaturesData, seoData, initialHomeData]);

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": headerData?.company_name || "The Dentofacial Hub",
    "url": process.env.NEXT_PUBLIC_BASE_URL,
    "logo": headerData?.logo,
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": headerData?.location?.mobile_number,
      "contactType": "customer service"
    }
  };

  // Only show loader if we TRULY have no data (e.g. first mount if server fetch failed)
  if ((pageData.homePageLoading && !initialHomeData) || (pageData.fetchDataLoading && !reduxFeaturesData)) {
    return <DotLoaders />;
  }

  // Use treatmentData (with images) if available, otherwise fallback to navigation categories
  const displayServices = (treatmentData && treatmentData.length > 0) ? treatmentData : pageData.serviceSection;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
      />
      <HeroSection content={pageData.bannerContents} imageUrl={pageData.imageUrl} />
      <AboutUsSection content={pageData.aboutContents} imageUrl={pageData.imageUrl} isHome={true} />
      <ServiceSection
        sectionContents={pageData.servicesContents}
        content={displayServices}
        marketArea={pageData.defaultMarketArea?.slug}
        imageUrl={pageData.categoryImageUrl || pageData.imageUrl}
      />
      <WhyChooseUs
        content={pageData.whyUsContents}
        imageUrl={pageData.imageUrl}
        itemImageUrl={pageData.itemImageUrl}
        subSections={pageData.whyUsSubSections}
      />
      <TestimonialSection sectionContents={pageData.testimonialContents} />
      <BannerSection />
      <BlogsSection sectionContents={pageData.latestBlogsContents} />
      <FollowOn />
      <FaqSection/>
    </>
  );
};

export default Homepage;

